import { ref, watch, onMounted, onUnmounted } from 'vue'

type Theme = 'light' | 'dark'
type ContrastMode = 'normal' | 'high'

export function useTheme() {
  const theme = ref<Theme>(getStoredTheme())
  const contrastMode = ref<ContrastMode>(getStoredContrast())

  function getStoredTheme(): Theme {
    const stored = localStorage.getItem('theme') as Theme | null
    if (stored === 'light' || stored === 'dark') return stored
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  }

  function getStoredContrast(): ContrastMode {
    const stored = localStorage.getItem('contrast-mode') as ContrastMode | null
    if (stored === 'normal' || stored === 'high') return stored
    return 'normal'
  }

  function applyTheme(t: Theme) {
    document.documentElement.classList.toggle('dark', t === 'dark')
    localStorage.setItem('theme', t)
  }

  function applyContrast(mode: ContrastMode) {
    document.documentElement.classList.toggle('high-contrast', mode === 'high')
    localStorage.setItem('contrast-mode', mode)
  }

  function toggleTheme() {
    theme.value = theme.value === 'light' ? 'dark' : 'light'
  }

  function toggleContrast() {
    contrastMode.value = contrastMode.value === 'normal' ? 'high' : 'normal'
  }

  function setTheme(t: Theme) {
    theme.value = t
  }

  function setContrast(mode: ContrastMode) {
    contrastMode.value = mode
  }

  let mediaQueryHandler: ((e: MediaQueryListEvent) => void) | null = null

  onMounted(() => {
    watch(theme, applyTheme, { immediate: true })
    watch(contrastMode, applyContrast, { immediate: true })

    mediaQueryHandler = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('theme')) {
        theme.value = e.matches ? 'dark' : 'light'
      }
    }
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', mediaQueryHandler)
  })

  onUnmounted(() => {
    if (mediaQueryHandler) {
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', mediaQueryHandler)
    }
  })

  return {
    theme,
    contrastMode,
    toggleTheme,
    toggleContrast,
    setTheme,
    setContrast,
  }
}
